/**
 * The registry of every customer-facing EMAIL KIND a tenant can design — ONE
 * place for what the frontend lists (labels, groups), what the builder offers
 * (merge fields + sample values, the locked call-to-action), what publish
 * enforces (required blocks, supported tokens) and what the send renders when
 * the tenant has not published a design of its own (the factory default:
 * subject + block layout that reproduce the email the platform always sent).
 *
 * Adding a kind = one entry here + the send site building its merge context.
 *
 * Runtime values live here (`.cjs`); the types in the sibling `.d.cts`.
 */

// ── Merge fields: label + sample per token (the builder palette + preview) ──
const BUSINESS_FIELDS = [
  "business.displayName",
  "business.legalName",
  "business.address",
  "business.phone",
  "business.email",
  "business.website",
  "business.taxId",
  "business.tagline",
  "business.license",
  "business.logo",
];

const MERGE_FIELD_LABELS = {
  "business.displayName": "Business name",
  "business.legalName": "Legal name",
  "business.address": "Address",
  "business.phone": "Phone",
  "business.email": "Email",
  "business.website": "Website",
  "business.taxId": "Tax ID",
  "business.tagline": "Tagline",
  "business.license": "License #",
  "business.logo": "Logo (image)",
  "customer.name": "Name",
  "customer.firstName": "First name",
  "customer.lastName": "Last name",
  "estimate.depositAmount": "Deposit amount",
  "estimate.discountAmount": "Discount amount",
  "customer.address": "Address",
  "estimate.number": "Number",
  "estimate.date": "Date",
  "estimate.total": "Total",
  "estimate.viewUrl": "View link",
  "estimate.message": "Your note",
  "invoice.number": "Number",
  "invoice.date": "Date",
  "invoice.dueDate": "Due date",
  "invoice.total": "Total",
  "invoice.payUrl": "Pay link",
  "appointment.when": "Date & time",
  "appointment.technician": "Technician",
  "appointment.serviceType": "Service",
  "appointment.manageUrl": "Manage link",
  "appointment.message": "Confirmation line",
  "appointment.address": "Service address",
  "appointment.reference": "Job reference",
  "appointment.date": "Date",
  "appointment.window": "Arrival window",
  "appointment.confirmUrl": "Confirm link",
  "estimate.scopeSummary": "Scope summary",
  "proposal.scopeSummary": "Scope summary",
  "proposal.recommendedTier": "Recommended tier",
  "invoice.balanceDue": "Balance due",
  "invoice.daysPastDue": "Days past due",
  "recovery.subject": "Service or membership",
  "recovery.kind": "job / membership",
  "recovery.failureReason": "Failure reason",
  "reminder.attempt": "Reminder number",
  "reminder.of": "Reminders in total",
  "receipt.number": "Receipt number",
  "receipt.date": "Date",
  "receipt.total": "Amount paid",
  "receipt.viewUrl": "Receipt link",
  "proposal.number": "Proposal number",
  "proposal.viewUrl": "Proposal link",
  "changeOrder.number": "Change order number",
  "changeOrder.date": "Date",
  "changeOrder.total": "Net change",
  "changeOrder.note": "Reason / note",
  "changeOrder.viewUrl": "Review link",
  "booking.instruction": "Instruction line",
  "booking.ctaLabel": "Button label",
  "booking.service": "Service",
  "booking.url": "Booking link",
  "booking.expires": "Link expiry",
  "form.name": "Form name",
  "form.url": "Form link",
  "form.expires": "Link expiry",
  "review.service": "Service",
  "review.url": "Rate link",
  "portal.url": "Portal link",
  "portal.expires": "Link expiry",
  "membership.planName": "Plan",
  "membership.renewDate": "Renewal date",
  "membership.autoRenew": "Auto-renew (on/off)",
  "membership.price": "Price",
  "membership.manageUrl": "Manage link",
  "membership.daysBefore": "Days before renewal",
  "renewal.serviceName": "Service",
  "renewal.renewDate": "Renewal date",
  "renewal.rate": "Rate",
  "renewal.optOutBy": "Opt-out deadline",
  "renewal.optOutUrl": "Opt-out link",
  "renewal.startDate": "Start date",
  "renewal.authorizeUrl": "Authorize link",
  "payment.kind": "Deposit / payment",
  "payment.amount": "Amount",
  "payment.jobName": "Job",
  "payment.jobNumber": "Job number",
  "payment.url": "Pay link",
  "payment.expires": "Link expiry",
  "paymentMethod.url": "Secure link",
  "paymentMethod.expires": "Link expiry",
  "paymentMethod.serviceName": "Service",
  "verify.maskedPhone": "Phone (masked)",
  "verify.url": "Confirm link",
  "report.title": "Report title",
  "report.url": "Report link",
  "legal.financing": "Financing disclosure",
  "legal.reviewFooter": "Review-request footer",
  "legal.paymentSafety": "Payment safety notice",
};

const MERGE_FIELD_SAMPLES = {
  "customer.name": "Dana Customer",
  "customer.firstName": "Dana",
  "customer.lastName": "Reyes",
  "estimate.depositAmount": "$300.00",
  "estimate.discountAmount": "$0.00",
  "customer.address": "742 Evergreen Terrace, Springfield",
  "estimate.number": "EST-2087",
  "estimate.date": "Aug 30, 2026",
  "estimate.total": "$1,240.00",
  "estimate.viewUrl": "#",
  "estimate.message": "Thanks for the opportunity — details below.",
  "invoice.number": "INV-1042",
  "invoice.date": "Aug 30, 2026",
  "invoice.dueDate": "Sep 13, 2026",
  "invoice.total": "$1,240.00",
  "invoice.payUrl": "#",
  "appointment.when": "Tue, Sep 8, 2:00 PM CDT",
  "appointment.technician": "Alex Torres",
  "appointment.serviceType": "AC Repair",
  "appointment.manageUrl": "#",
  "appointment.message": "Your appointment is confirmed for Tue, Sep 8, 2:00 PM CDT.",
  "appointment.address": "742 Evergreen Terrace, Springfield, IL 62704",
  "appointment.reference": "JOB-4821",
  "appointment.date": "Tue, Sep 8",
  "appointment.window": "8:00 AM – 10:00 AM CDT",
  "appointment.confirmUrl": "#",
  "estimate.scopeSummary": "a new heat pump",
  "proposal.scopeSummary": "a new heat pump",
  "proposal.recommendedTier": "Better",
  "invoice.balanceDue": "$1,240.00",
  "invoice.daysPastDue": "3",
  "recovery.subject": "Quarterly filter service",
  "recovery.kind": "job",
  "recovery.failureReason": "Card declined",
  "reminder.attempt": "1",
  "reminder.of": "2",
  "receipt.number": "RCPT-0031",
  "receipt.date": "Aug 30, 2026",
  "receipt.total": "$620.00",
  "receipt.viewUrl": "#",
  "proposal.number": "PROP-0012",
  "proposal.viewUrl": "#",
  "changeOrder.number": "CO-7",
  "changeOrder.date": "Aug 30, 2026",
  "changeOrder.total": "$180.00",
  "changeOrder.note": "Added a second return duct as discussed on site.",
  "changeOrder.viewUrl": "#",
  "booking.instruction": "Your Company is ready to schedule AC Tune-up. Pick a time that works for you:",
  "booking.ctaLabel": "Schedule your appointment",
  "booking.service": "AC Tune-up",
  "booking.url": "#",
  "booking.expires": "September 14, 2026",
  "form.name": "Pre-visit questionnaire",
  "form.url": "#",
  "form.expires": "September 25, 2026",
  "review.service": "AC Tune-up",
  "review.url": "#",
  "portal.url": "#",
  "portal.expires": "Sep 14, 2026, 5:00 PM",
  "membership.planName": "Comfort Club",
  "membership.renewDate": "October 1, 2026",
  "membership.autoRenew": "on",
  "membership.price": "$199.00 / year",
  "membership.manageUrl": "#",
  "membership.daysBefore": "14",
  "renewal.serviceName": "Quarterly filter service",
  "renewal.renewDate": "October 1, 2026",
  "renewal.rate": "$89.00",
  "renewal.optOutBy": "September 24, 2026",
  "renewal.optOutUrl": "#",
  "renewal.startDate": "October 1, 2026",
  "renewal.authorizeUrl": "#",
  "payment.kind": "deposit",
  "payment.amount": "$250.00",
  "payment.jobName": "Furnace replacement",
  "payment.jobNumber": "JOB-4821",
  "payment.url": "#",
  "payment.expires": "Sep 8, 2026, 5:00 PM",
  "paymentMethod.url": "#",
  "paymentMethod.expires": "24 hours",
  "paymentMethod.serviceName": "Quarterly filter service",
  "verify.maskedPhone": "(•••) •••-0142",
  "verify.url": "#",
  "report.title": "Spring maintenance visit",
  "report.url": "#",
  "legal.financing": "",
  "legal.reviewFooter": "Your Company\n123 Main St, Springfield, IL 62704\nPrefer not to get review requests? Reply STOP, or ask us to update your contact preferences and we will stop asking.",
  "legal.paymentSafety": "Never share card details over text or email — pay safely on the linked page.",
};

/** CTA button action → the merge token its href resolves to. */
const CTA_ACTIONS = {
  estimateView: "estimate.viewUrl",
  invoicePay: "invoice.payUrl",
  appointmentManage: "appointment.manageUrl",
  receiptView: "receipt.viewUrl",
  proposalView: "proposal.viewUrl",
  changeOrderView: "changeOrder.viewUrl",
  bookingOpen: "booking.url",
  reviewRate: "review.url",
  portalOpen: "portal.url",
  membershipManage: "membership.manageUrl",
  renewalOptOut: "renewal.optOutUrl",
  renewalAuthorize: "renewal.authorizeUrl",
  paymentPay: "payment.url",
  paymentMethodOpen: "paymentMethod.url",
  phoneConfirm: "verify.url",
  reportView: "report.url",
  appointmentConfirm: "appointment.confirmUrl",
  formOpen: "form.url",
};

const CUSTOMER = ["customer.name", "customer.firstName"];
const HI = "<p>Hi {{customer.firstName}},</p>";

// ── The kinds ──────────────────────────────────────────────────────────────
// `body` is the factory greeting/message rich text; `legal` a required locked
// legal block token (rendered muted, pre-line; empty when the send has nothing
// to disclose); `cta` the locked button (null = the email has no link).
const EMAIL_KINDS = {
  ESTIMATE_SEND: {
    label: "Estimate emails", surface: "Estimate email", noun: "estimate", group: "Documents",
    cta: { action: "estimateView", label: "View estimate", menuLabel: "Open the estimate" },
    legal: "legal.financing",
    fields: [...CUSTOMER, "customer.lastName", "customer.address", "estimate.number", "estimate.date", "estimate.total", "estimate.depositAmount", "estimate.discountAmount", "estimate.viewUrl", "estimate.message", "legal.financing"],
    subject: "Your estimate {{estimate.number}} from {{business.displayName}}",
    body: "<p>Hi {{customer.name}},</p><p>Your estimate <strong>{{estimate.number}}</strong> for <strong>{{estimate.total}}</strong> is ready to review.</p>",
    closing: "<p>We look forward to working with you.</p>",
  },
  INVOICE_SEND: {
    label: "Invoice emails", surface: "Invoice email", noun: "invoice", group: "Documents",
    cta: { action: "invoicePay", label: "Pay invoice", menuLabel: "Pay the invoice" },
    legal: "legal.financing",
    fields: [...CUSTOMER, "customer.address", "invoice.number", "invoice.date", "invoice.dueDate", "invoice.total", "invoice.payUrl", "legal.financing"],
    subject: "Invoice {{invoice.number}} from {{business.displayName}}",
    body: "<p>Hi {{customer.name}},</p><p>Here is invoice <strong>{{invoice.number}}</strong> for <strong>{{invoice.total}}</strong>, dated {{invoice.date}}.</p>",
    closing: "<p>Thank you for your business.</p>",
  },
  RECEIPT: {
    label: "Payment receipts", surface: "Payment receipt", noun: "receipt", group: "Payments",
    cta: { action: "receiptView", label: "View receipt", menuLabel: "Open the receipt" },
    legal: null,
    fields: [...CUSTOMER, "customer.address", "receipt.number", "receipt.date", "receipt.total", "receipt.viewUrl"],
    subject: "Receipt {{receipt.number}} from {{business.displayName}}",
    body: "<p>Hi {{customer.name}},</p><p>Your receipt <strong>{{receipt.number}}</strong> for <strong>{{receipt.total}}</strong> is available below.</p>",
    closing: "<p>Thank you,<br>{{business.displayName}}</p>",
  },
  PROPOSAL_SEND: {
    label: "Proposal emails", surface: "Proposal email", noun: "proposal", group: "Documents",
    cta: { action: "proposalView", label: "Review proposal", menuLabel: "Open the proposal" },
    legal: null,
    fields: [...CUSTOMER, "proposal.number", "proposal.viewUrl"],
    subject: "Your proposal {{proposal.number}} from {{business.displayName}}",
    body: "<p>Hi {{customer.name}},</p><p>Proposal <strong>{{proposal.number}}</strong> is ready for your review. Open it to compare the options and choose the one that works for you.</p>",
    closing: "",
  },
  CHANGE_ORDER: {
    label: "Change order emails", surface: "Change order email", noun: "change order", group: "Documents",
    cta: { action: "changeOrderView", label: "Review change order", menuLabel: "Open the change order" },
    legal: null,
    fields: [...CUSTOMER, "changeOrder.number", "changeOrder.date", "changeOrder.total", "changeOrder.note", "changeOrder.viewUrl"],
    subject: "{{business.displayName}}: Change Order {{changeOrder.number}}",
    body: "<p>Hi {{customer.name}},</p><p>A change order for your job is ready for your review. Net change: <strong>{{changeOrder.total}}</strong>.</p><p>{{changeOrder.note}}</p>",
    closing: "",
  },
  JOB_CONFIRMATION: {
    label: "Job confirmation emails", surface: "Job confirmation", noun: "job confirmation", group: "Scheduling",
    cta: { action: "appointmentManage", label: "View or change your appointment", menuLabel: "View or change the appointment" },
    legal: null,
    fields: [...CUSTOMER, "appointment.when", "appointment.technician", "appointment.serviceType", "appointment.manageUrl", "appointment.message"],
    subject: "Your appointment is confirmed — {{business.displayName}}",
    body: "<p>Hi {{customer.name}},</p><p>{{appointment.message}}</p>",
    closing: "<p>We look forward to seeing you.</p>",
  },
  SCHEDULE_REQUEST: {
    label: "Book-a-time emails", surface: "Book-a-time email", noun: "booking request", group: "Scheduling",
    cta: { action: "bookingOpen", label: "{{booking.ctaLabel}}", menuLabel: "Open the booking page" },
    legal: null,
    fields: [...CUSTOMER, "booking.instruction", "booking.ctaLabel", "booking.service", "booking.url", "booking.expires"],
    subject: "{{booking.ctaLabel}} with {{business.displayName}}",
    body: "<p>Hi {{customer.name}},</p><p>{{booking.instruction}}</p>",
    closing: "<p>This link expires on {{booking.expires}}.</p>",
  },
  VISIT_CONFIRMED: {
    label: "Visit confirmed emails", surface: "Visit confirmed", noun: "visit confirmation", group: "Scheduling",
    cta: null,
    legal: null,
    fields: [...CUSTOMER, "appointment.when", "appointment.serviceType", "appointment.address", "appointment.reference"],
    subject: "Your visit is confirmed — {{appointment.serviceType}}",
    body: HI + "<p>Your <strong>{{appointment.serviceType}}</strong> visit with {{business.displayName}} is confirmed for <strong>{{appointment.when}}</strong>.</p><p>Service address: {{appointment.address}}</p><p>Reference: {{appointment.reference}}</p>",
    closing: "<p>Reply to this conversation or contact the office if you need to reschedule.</p>",
  },
  REVIEW_REQUEST: {
    label: "Review request emails", surface: "Review request", noun: "review request", group: "Reviews",
    cta: { action: "reviewRate", label: "Rate your visit (1-5★)", menuLabel: "Open the review page" },
    legal: "legal.reviewFooter",
    fields: [...CUSTOMER, "customer.lastName", "review.service", "review.url", "appointment.technician", "reminder.attempt", "reminder.of", "legal.reviewFooter"],
    subject: "How was {{review.service}}?",
    body: HI + "<p>Thanks for choosing us. How did we do?</p>",
    closing: "",
  },
  REVIEW_FEEDBACK_ACK: {
    label: "Review feedback replies", surface: "Review feedback reply", noun: "review reply", group: "Reviews",
    cta: null,
    legal: null,
    fields: [...CUSTOMER],
    subject: "Thanks for your feedback",
    body: HI + "<p>Thank you for the honest feedback — we're sorry we missed the mark. Our team has received it and will follow up if needed.</p>",
    closing: "",
  },
  PORTAL_INVITATION: {
    label: "Portal invitations", surface: "Portal invitation", noun: "portal invitation", group: "Customer portal",
    cta: { action: "portalOpen", label: "Activate your account", menuLabel: "Open the account setup page" },
    legal: null,
    fields: [...CUSTOMER, "portal.url", "portal.expires"],
    subject: "Set up your {{business.displayName}} account",
    body: "<h2>Welcome to your customer portal</h2><p>{{business.displayName}} invited you to access your customer portal. Verify your email and create a password to get started.</p>",
    closing: "<p>This invitation expires {{portal.expires}}. If you weren't expecting this invitation, you can ignore this email.</p>",
  },
  PORTAL_VERIFY: {
    label: "Portal sign-up verification", surface: "Portal verification", noun: "portal verification", group: "Customer portal",
    cta: { action: "portalOpen", label: "Verify your email", menuLabel: "Open the verification page" },
    legal: null,
    fields: [...CUSTOMER, "portal.url", "portal.expires"],
    subject: "Set up your {{business.displayName}} account",
    body: "<p>Welcome to your {{business.displayName}} customer portal.</p><p>Click below to verify your email and create a password:</p>",
    closing: "<p>This link expires {{portal.expires}}. If you didn't request this, you can ignore this email.</p>",
  },
  PORTAL_PASSWORD_RESET: {
    label: "Portal password resets", surface: "Portal password reset", noun: "password reset", group: "Customer portal",
    cta: { action: "portalOpen", label: "Reset your password", menuLabel: "Open the reset page" },
    legal: null,
    fields: [...CUSTOMER, "portal.url", "portal.expires"],
    subject: "Reset your {{business.displayName}} portal password",
    body: HI + "<p>We received a request to reset your {{business.displayName}} portal password.</p>",
    closing: "<p>This link expires {{portal.expires}}. If you didn't request this, you can ignore this email.</p>",
  },
  MEMBERSHIP_RENEWAL: {
    label: "Membership renewal notices", surface: "Membership renewal", noun: "membership renewal notice", group: "Memberships",
    cta: { action: "membershipManage", label: "Manage membership", menuLabel: "Open the membership" },
    legal: null,
    fields: [...CUSTOMER, "membership.planName", "membership.renewDate", "membership.autoRenew", "membership.price", "membership.manageUrl", "membership.daysBefore"],
    subject: "{{membership.planName}} renews on {{membership.renewDate}}",
    body: HI + "<p>Your <strong>{{membership.planName}}</strong> membership renewal date is <strong>{{membership.renewDate}}</strong>.</p><p>Auto-renew is <strong>{{membership.autoRenew}}</strong>. Price: {{membership.price}}.</p><p>You can review or manage this membership here:</p>",
    closing: "<p>This reminder was sent {{membership.daysBefore}} day(s) before renewal.</p>",
  },
  RECURRING_RENEWAL_NOTICE: {
    label: "Recurring service renewals", surface: "Recurring renewal", noun: "renewal notice", group: "Memberships",
    cta: { action: "renewalOptOut", label: "Turn off auto-renewal", menuLabel: "Open the opt-out page" },
    legal: null,
    fields: [...CUSTOMER, "renewal.serviceName", "renewal.renewDate", "renewal.rate", "renewal.optOutBy", "renewal.optOutUrl"],
    subject: "Your recurring service \"{{renewal.serviceName}}\" renews on {{renewal.renewDate}}",
    body: HI + "<p>Your recurring service <strong>{{renewal.serviceName}}</strong> will automatically renew on <strong>{{renewal.renewDate}}</strong>. Your rate: <strong>{{renewal.rate}}</strong>. Pricing may be adjusted per your agreement.</p><p>If you'd rather not renew, opt out by <strong>{{renewal.optOutBy}}</strong> — your current term finishes and the series won't roll into a new one:</p>",
    closing: "",
  },
  RECURRING_RENEWAL_AUTHORIZATION: {
    label: "Renewal authorizations", surface: "Renewal authorization", noun: "renewal authorization", group: "Memberships",
    cta: { action: "renewalAuthorize", label: "Review & authorize renewal", menuLabel: "Open the authorization page" },
    legal: null,
    fields: [...CUSTOMER, "renewal.serviceName", "renewal.startDate", "renewal.rate", "renewal.authorizeUrl"],
    subject: "Action needed: authorize the renewal of \"{{renewal.serviceName}}\"",
    body: HI + "<p>Your recurring service <strong>{{renewal.serviceName}}</strong> is ready to renew, but the billing authorization on file has expired.</p><p>Start date: <strong>{{renewal.startDate}}</strong>. Rate: <strong>{{renewal.rate}}</strong>.</p><p>Please confirm so service and automatic billing continue without interruption:</p>",
    closing: "",
  },
  PAYMENT_REQUEST: {
    label: "Payment & deposit requests", surface: "Payment request", noun: "payment request", group: "Payments",
    cta: { action: "paymentPay", label: "Pay securely", menuLabel: "Open the payment page" },
    legal: "legal.paymentSafety",
    fields: [...CUSTOMER, "payment.kind", "payment.amount", "payment.jobName", "payment.jobNumber", "payment.url", "payment.expires", "legal.paymentSafety"],
    subject: "{{payment.kind}} request for {{payment.jobName}} — {{payment.amount}}",
    body: "<p>Hi,</p><p>Here is your secure {{payment.kind}} link for <strong>{{payment.jobName}}</strong> ({{payment.jobNumber}}), for <strong>{{payment.amount}}</strong>:</p>",
    closing: "<p>The link expires {{payment.expires}}.</p>",
  },
  PAYMENT_METHOD_REQUEST: {
    label: "Add-payment-method requests", surface: "Payment method request", noun: "payment method request", group: "Payments",
    cta: { action: "paymentMethodOpen", label: "Add Payment Method", menuLabel: "Open the secure page" },
    legal: null,
    fields: [...CUSTOMER, "paymentMethod.url", "paymentMethod.expires"],
    subject: "Add your payment method for {{customer.name}}",
    body: "<p>Hello {{customer.firstName}},</p><p>Use the secure button below to add your payment method for {{customer.name}}.</p>",
    closing: "<p>This link expires in {{paymentMethod.expires}}.</p>",
  },
  PAYMENT_METHOD_REPAIR: {
    label: "Update-payment-method requests", surface: "Payment method update", noun: "payment method update", group: "Payments",
    cta: { action: "paymentMethodOpen", label: "Update Payment Method", menuLabel: "Open the secure page" },
    legal: null,
    fields: [...CUSTOMER, "paymentMethod.url", "paymentMethod.expires", "paymentMethod.serviceName"],
    subject: "Update your payment method for {{paymentMethod.serviceName}}",
    body: "<p>Your service provider needs an updated payment method to continue recurring billing for <strong>{{paymentMethod.serviceName}}</strong>.</p><p>Use the secure button below to save a new card:</p>",
    closing: "<p>This link will expire on {{paymentMethod.expires}}.</p>",
  },
  PHONE_VERIFICATION: {
    label: "Phone number confirmations", surface: "Phone confirmation", noun: "phone confirmation", group: "Customer portal",
    cta: { action: "phoneConfirm", label: "Yes, that's my number", menuLabel: "Open the confirmation page" },
    legal: null,
    fields: [...CUSTOMER, "verify.maskedPhone", "verify.url"],
    subject: "Is {{verify.maskedPhone}} your phone number? — {{business.displayName}}",
    body: HI + "<p>Someone texting {{business.displayName}} from <strong>{{verify.maskedPhone}}</strong> gave this email address as theirs.</p><p>If that was you, confirm below and we will add the number to your account so your bookings and messages stay together:</p>",
    closing: "<p>If you don't recognise this number, ignore this email — nothing will change on your account. The link expires in 24 hours.</p>",
  },
  REPORT_READY: {
    label: "Report delivery emails", surface: "Report delivery", noun: "report", group: "Reports",
    cta: { action: "reportView", label: "View report", menuLabel: "Open the report" },
    legal: null,
    fields: [...CUSTOMER, "report.title", "report.url"],
    subject: "Your report from {{business.displayName}}: {{report.title}}",
    body: "<p>Your report <strong>{{report.title}}</strong> is ready.</p>",
    closing: "<p>Copy or share this link: {{report.url}}</p>",
  },
  REPORT_REMINDER: {
    label: "Report reminder emails", surface: "Report reminder", noun: "report reminder", group: "Reports",
    cta: { action: "reportView", label: "View report", menuLabel: "Open the report" },
    legal: null,
    fields: [...CUSTOMER, "report.title", "report.url"],
    subject: "Reminder: your report from {{business.displayName}} is ready",
    body: "<p>Just a reminder — your report <strong>{{report.title}}</strong> is ready to view.</p>",
    closing: "<p>Copy or share this link: {{report.url}}</p>",
  },
  // ── Reminder-engine emails (Settings → Reminders owns the cadence; the copy lives here) ──
  APPOINTMENT_CONFIRM_REQUEST: {
    label: "Appointment confirmation requests", surface: "Confirmation request", noun: "confirmation request", group: "Reminders",
    cta: { action: "appointmentConfirm", label: "Confirm your appointment", menuLabel: "Open the confirmation link" },
    legal: "legal.financing",
    fields: [...CUSTOMER, "customer.lastName", "appointment.date", "appointment.window", "appointment.when", "appointment.serviceType", "appointment.technician", "appointment.confirmUrl", "legal.financing"],
    subject: "Confirm your appointment with {{business.displayName}}",
    body: HI + "<p>You're on the schedule with {{business.displayName}} for <strong>{{appointment.date}}</strong>, arrival window <strong>{{appointment.window}}</strong>.</p><p>Confirm in one tap:</p>",
    closing: "<p>Can't make it? Reschedule or cancel from that same link.</p><p>See you then,<br>{{business.displayName}}</p>",
  },
  ESTIMATE_FOLLOW_UP: {
    label: "Estimate follow-ups", surface: "Estimate follow-up", noun: "estimate follow-up", group: "Reminders",
    cta: { action: "estimateView", label: "View estimate", menuLabel: "Open the estimate" },
    legal: "legal.financing",
    fields: [...CUSTOMER, "customer.lastName", "estimate.number", "estimate.date", "estimate.total", "estimate.viewUrl", "estimate.scopeSummary", "reminder.attempt", "reminder.of", "legal.financing"],
    subject: "Following up on your estimate from {{business.displayName}}",
    body: HI + "<p>Just checking in to see if you have any questions about the estimate we sent on {{estimate.date}} for <strong>{{estimate.total}}</strong>. You can view, ask questions, or approve it here:</p>",
    closing: "<p>If pricing or scope is a concern, just reply to this email — happy to walk through it or revise.</p><p>— {{business.displayName}}</p>",
  },
  PROPOSAL_FOLLOW_UP: {
    label: "Proposal follow-ups", surface: "Proposal follow-up", noun: "proposal follow-up", group: "Reminders",
    cta: { action: "proposalView", label: "View your options", menuLabel: "Open the proposal" },
    legal: null,
    fields: [...CUSTOMER, "customer.lastName", "proposal.number", "proposal.viewUrl", "proposal.scopeSummary", "proposal.recommendedTier", "reminder.attempt", "reminder.of"],
    subject: "Your options from {{business.displayName}}",
    body: HI + "<p>A quick reminder of the options we put together for {{proposal.scopeSummary}}. Most customers choose the {{proposal.recommendedTier}} tier — view all options here:</p>",
    closing: "<p>Questions about scope, pricing, or timing? Just reply — we are happy to walk through any of the tiers.</p><p>— {{business.displayName}}</p>",
  },
  INVOICE_PAYMENT_REMINDER: {
    label: "Unpaid invoice reminders", surface: "Invoice payment reminder", noun: "invoice reminder", group: "Reminders",
    cta: { action: "invoicePay", label: "Pay invoice", menuLabel: "Pay the invoice" },
    legal: "legal.financing",
    fields: [...CUSTOMER, "customer.lastName", "invoice.number", "invoice.total", "invoice.balanceDue", "invoice.dueDate", "invoice.payUrl", "invoice.daysPastDue", "reminder.attempt", "reminder.of", "legal.financing"],
    subject: "Invoice {{invoice.number}} from {{business.displayName}} — payment reminder",
    body: HI + "<p>A quick reminder that invoice <strong>{{invoice.number}}</strong> for <strong>{{invoice.balanceDue}}</strong> is due. Pay securely here:</p>",
    closing: "<p>— {{business.displayName}}</p>",
  },
  DEPOSIT_REMINDER: {
    label: "Unpaid deposit reminders", surface: "Deposit reminder", noun: "deposit reminder", group: "Reminders",
    cta: { action: "paymentPay", label: "Pay deposit", menuLabel: "Open the payment page" },
    legal: "legal.paymentSafety",
    fields: [...CUSTOMER, "customer.lastName", "payment.kind", "payment.amount", "payment.url", "appointment.date", "appointment.window", "appointment.when", "legal.paymentSafety"],
    subject: "Deposit needed to confirm your appointment with {{business.displayName}}",
    body: HI + "<p>A deposit of <strong>{{payment.amount}}</strong> secures your appointment on <strong>{{appointment.date}}</strong> ({{appointment.window}}). Pay here:</p>",
    closing: "<p>— {{business.displayName}}</p>",
  },
  PAYMENT_FAILED: {
    label: "Payment failed notices", surface: "Payment failed", noun: "payment failure notice", group: "Reminders",
    cta: { action: "paymentMethodOpen", label: "Update payment method", menuLabel: "Open the secure page" },
    legal: null,
    fields: [...CUSTOMER, "customer.lastName", "recovery.subject", "recovery.kind", "recovery.failureReason", "paymentMethod.url", "paymentMethod.expires", "reminder.attempt", "reminder.of"],
    subject: "We could not process your payment for {{recovery.subject}}",
    body: HI + "<p>We tried to process the payment for <strong>{{recovery.subject}}</strong>, but it did not go through. Please update your payment method so service continues without interruption:</p>",
    closing: "<p>— {{business.displayName}}</p>",
  },
  SERVICE_SUSPENDED: {
    label: "Service suspended notices", surface: "Service suspended", noun: "suspension notice", group: "Reminders",
    cta: { action: "paymentMethodOpen", label: "Update payment method", menuLabel: "Open the secure page" },
    legal: null,
    fields: [...CUSTOMER, "customer.lastName", "recovery.subject", "recovery.kind", "paymentMethod.url", "paymentMethod.expires"],
    subject: "{{recovery.subject}} is suspended for non-payment",
    body: HI + "<p><strong>{{recovery.subject}}</strong> is paused until the outstanding payment is settled. Update your payment method to resume service:</p>",
    closing: "<p>— {{business.displayName}}</p>",
  },
  PAYMENT_FINAL_NOTICE: {
    label: "Final notices before cancellation", surface: "Final notice", noun: "final notice", group: "Reminders",
    cta: { action: "paymentMethodOpen", label: "Update payment method", menuLabel: "Open the secure page" },
    legal: null,
    fields: [...CUSTOMER, "customer.lastName", "recovery.subject", "recovery.kind", "paymentMethod.url", "paymentMethod.expires"],
    subject: "Final notice before cancellation",
    body: HI + "<p>This is our final notice: <strong>{{recovery.subject}}</strong> will be cancelled unless the outstanding payment is completed. Update your payment method now to keep it:</p>",
    closing: "<p>— {{business.displayName}}</p>",
  },
  PAYMENT_RECOVERED: {
    label: "Payment recovered notices", surface: "Payment recovered", noun: "recovery notice", group: "Reminders",
    cta: null,
    legal: null,
    fields: [...CUSTOMER, "customer.lastName", "recovery.subject", "recovery.kind"],
    subject: "Payment received — {{recovery.subject}} is active again",
    body: HI + "<p>Thank you — your payment went through and <strong>{{recovery.subject}}</strong> is active again.</p>",
    closing: "<p>— {{business.displayName}}</p>",
  },
  SERVICE_CANCELLED: {
    label: "Service cancelled notices", surface: "Service cancelled", noun: "cancellation notice", group: "Reminders",
    cta: null,
    legal: null,
    fields: [...CUSTOMER, "customer.lastName", "recovery.subject", "recovery.kind"],
    subject: "{{recovery.subject}} has been cancelled",
    body: HI + "<p><strong>{{recovery.subject}}</strong> has been cancelled after repeated payment failures. If you'd like to reinstate it, reply to this email or give us a call.</p>",
    closing: "<p>— {{business.displayName}}</p>",
  },
  // A checklist or form on a job that the customer completes themselves, from
  // their own phone, through a link the office sends. The button carries the
  // link; there is no customer login, so this email is the only way it arrives.
  FORM_REQUEST: {
    label: "Form request emails", surface: "Form request", noun: "form request", group: "Scheduling",
    cta: { action: "formOpen", label: "Open the form", menuLabel: "Open the form" },
    legal: null,
    fields: [...CUSTOMER, "customer.lastName", "form.name", "form.url", "form.expires"],
    subject: "{{business.displayName}}: please complete {{form.name}}",
    body: HI + "<p>Please complete <strong>{{form.name}}</strong>. It only takes a few minutes, and you can do it from your phone.</p>",
    closing: "<p>This link expires on {{form.expires}}.</p>",
  },
};

const EMAIL_TEMPLATE_KINDS = Object.keys(EMAIL_KINDS);
const EMAIL_KIND_GROUPS = ["Documents", "Payments", "Scheduling", "Customer portal", "Memberships", "Reviews", "Reports", "Reminders"];

/**
 * Which design each reminder-engine EMAIL step renders through, by reminder
 * type and template key. A key missing here (the two staff-audience flags) is
 * not a customer email. Several template keys collapse onto one kind: the
 * step number travels as `{{reminder.attempt}}` / `{{reminder.of}}` and the
 * job-vs-membership variant as `{{recovery.kind}}` / `{{recovery.subject}}`.
 */
const REMINDER_EMAIL_KINDS = {
  JOB_CONFIRMATION: { "jc-initial-email": "APPOINTMENT_CONFIRM_REQUEST" },
  ESTIMATE_FOLLOWUP: { "ef-day2-email": "ESTIMATE_FOLLOW_UP", "ef-day5-email": "ESTIMATE_FOLLOW_UP" },
  PROPOSAL_FOLLOWUP: { "pf-day2-email": "PROPOSAL_FOLLOW_UP", "pf-day5-email": "PROPOSAL_FOLLOW_UP" },
  UNPAID_INVOICE: { "ui-day0-email": "INVOICE_PAYMENT_REMINDER", "ui-day3-email": "INVOICE_PAYMENT_REMINDER" },
  UNPAID_DEPOSIT: { "ud-issue-email": "DEPOSIT_REMINDER" },
  PAYMENT_RECOVERY: {
    "pr-first-fail-email": "PAYMENT_FAILED",
    "pr-day0-email": "PAYMENT_FAILED",
    "pr-day2-email": "PAYMENT_FAILED",
    "pr-membership-first-fail-email": "PAYMENT_FAILED",
    "pr-membership-first-fail-link-email": "PAYMENT_FAILED",
    "pr-membership-day0-email": "PAYMENT_FAILED",
    "pr-membership-day2-email": "PAYMENT_FAILED",
    "pr-day0-no-link-email": "PAYMENT_FAILED",
    "pr-day2-no-link-email": "PAYMENT_FAILED",
    "pr-suspended-email": "SERVICE_SUSPENDED",
    "pr-suspended-no-link-email": "SERVICE_SUSPENDED",
    "pr-membership-suspended-no-link-email": "SERVICE_SUSPENDED",
    "pr-final-email": "PAYMENT_FINAL_NOTICE",
    "pr-final-no-link-email": "PAYMENT_FINAL_NOTICE",
    "pr-membership-final-no-link-email": "PAYMENT_FINAL_NOTICE",
    "pr-recovered-email": "PAYMENT_RECOVERED",
    "pr-membership-recovered-email": "PAYMENT_RECOVERED",
    "pr-cancelled-email": "SERVICE_CANCELLED",
    "pr-membership-cancelled-email": "SERVICE_CANCELLED",
  },
  REVIEW_REQUEST: { "rr-initial-email": "REVIEW_REQUEST", "rr-day7-email": "REVIEW_REQUEST" },
};

/**
 * Reminder EMAIL steps addressed to the partner's own team (the office flags in
 * the unpaid-invoice / unpaid-deposit cadences). They keep their editable
 * subject / body in Settings → Reminders and go to the business mailbox, never
 * to the customer.
 */
const REMINDER_STAFF_TEMPLATE_KEYS = {
  UNPAID_INVOICE: ["ui-day21-manual"],
  UNPAID_DEPOSIT: ["ud-pre2-flag"],
};

/** Whether this (reminder type, template key) is one of the staff-audience office flags. */
function isStaffReminderTemplate(reminderType, templateKey) {
  const keys = REMINDER_STAFF_TEMPLATE_KEYS[reminderType];
  return !!keys && !!templateKey && keys.includes(templateKey);
}

/** The design kind a reminder EMAIL step renders through (null = not a customer design). */
function reminderEmailKind(reminderType, templateKey) {
  const byKey = REMINDER_EMAIL_KINDS[reminderType];
  return byKey && templateKey && byKey[templateKey] ? byKey[templateKey] : null;
}

/** Every token a kind's send populates (business fields + its own). */
function supportedFieldsFor(kind) {
  const k = EMAIL_KINDS[kind];
  return k ? [...BUSINESS_FIELDS, ...k.fields] : [];
}

/** Which token a kind's CTA resolves to (null when the kind has no link). */
function kindCtaToken(kind) {
  const k = EMAIL_KINDS[kind];
  return k && k.cta ? CTA_ACTIONS[k.cta.action] || null : null;
}

let seq = 0;
const nextId = (type) => `${type}-${++seq}`;

/**
 * The factory layout for a kind — header, greeting/body, [locked legal],
 * [locked CTA], closing, footer. What the send renders when the tenant has no
 * published design, and what "New email" starts from.
 */
function defaultEmailLayout(kind) {
  const k = EMAIL_KINDS[kind];
  if (!k) return { blocks: [] };
  const blocks = [
    { id: nextId("header"), type: "header", showLogo: true, html: "<p>{{business.displayName}}</p>", align: "left" },
    { id: nextId("text"), type: "text", html: k.body },
  ];
  if (k.cta) {
    blocks.push({ id: nextId("button"), type: "button", label: k.cta.label, action: k.cta.action, url: "", align: "left", locked: true });
  }
  if (k.closing) blocks.push({ id: nextId("text"), type: "text", html: k.closing });
  if (k.legal) blocks.push({ id: nextId("legal"), type: "legal", token: k.legal, locked: true });
  blocks.push({
    id: nextId("footer"),
    type: "footer",
    html: "<p>{{business.displayName}}</p><p>{{business.address}}</p><p>{{business.phone}} · {{business.email}}</p>",
  });
  return { blocks };
}

/** The factory subject for a kind. */
function defaultEmailSubject(kind) {
  const k = EMAIL_KINDS[kind];
  return k ? k.subject : "";
}

/** A preview context for a kind: every supported token with its sample value. */
function sampleContextFor(kind) {
  const out = {};
  for (const token of supportedFieldsFor(kind)) {
    if (token in MERGE_FIELD_SAMPLES) out[token] = MERGE_FIELD_SAMPLES[token];
  }
  return out;
}

module.exports = {
  EMAIL_KINDS,
  EMAIL_TEMPLATE_KINDS,
  EMAIL_KIND_GROUPS,
  REMINDER_EMAIL_KINDS,
  REMINDER_STAFF_TEMPLATE_KEYS,
  isStaffReminderTemplate,
  reminderEmailKind,
  BUSINESS_FIELDS,
  MERGE_FIELD_LABELS,
  MERGE_FIELD_SAMPLES,
  CTA_ACTIONS,
  supportedFieldsFor,
  kindCtaToken,
  defaultEmailLayout,
  defaultEmailSubject,
  sampleContextFor,
};
